import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { LifeBuoy, Loader2, ArrowLeft, X, MessageSquare } from "lucide-react";
import api from "../lib/api";
import TicketThread, { TYPE_LABELS, STATUS_LABELS, STATUS_COLORS, PRIORITY_LABELS, PRIORITY_COLORS } from "../components/tickets/TicketThread";

const fmtDate = (iso) => { try { return new Date(iso).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }); } catch { return iso; } };

export default function AdminTicketsPage() {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);
    const [statusFilter, setStatusFilter] = useState("");
    const [typeFilter, setTypeFilter] = useState("");

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (statusFilter) params.status = statusFilter;
            if (typeFilter) params.type = typeFilter;
            const { data } = await api.get("/tickets", { params });
            setTickets(data.tickets || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [statusFilter, typeFilter]);
    useEffect(() => { load(); }, [load]);

    const openCount = tickets.filter((t) => t.status === "open").length;

    return (
        <div className="max-w-5xl mx-auto px-4 py-8" data-testid="admin-tickets-page">
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#111827] mb-4" data-testid="admin-tickets-back">
                <ArrowLeft className="w-4 h-4" /> Zurück zum Admin-Dashboard
            </Link>
            <div className="flex items-center gap-3 mb-6">
                <LifeBuoy className="w-7 h-7 text-[#FFCC00]" />
                <div>
                    <h1 className="font-display text-2xl font-black">Support-Tickets</h1>
                    <p className="text-sm text-gray-500">{tickets.length} Tickets · {openCount} offen</p>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="h-9 rounded-sm border border-gray-300 px-2 text-sm" data-testid="admin-tickets-status-filter">
                    <option value="">Alle Status</option>
                    {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="h-9 rounded-sm border border-gray-300 px-2 text-sm" data-testid="admin-tickets-type-filter">
                    <option value="">Alle Typen</option>
                    {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
            </div>

            <div className="bg-white border border-gray-200 rounded-sm divide-y divide-gray-100">
                {loading ? (
                    <div className="flex items-center justify-center py-12 text-gray-400"><Loader2 className="w-6 h-6 animate-spin" /></div>
                ) : tickets.length === 0 ? (
                    <div className="px-6 py-12 text-center text-sm text-gray-400" data-testid="admin-tickets-empty">Keine Tickets gefunden.</div>
                ) : (
                    tickets.map((t) => (
                        <button key={t.id} onClick={() => setOpenId(t.id)} className="w-full text-left px-5 py-3 hover:bg-gray-50 flex items-center gap-3" data-testid={`admin-ticket-row-${t.id}`}>
                            <MessageSquare className="w-4 h-4 text-gray-300 shrink-0" />
                            <div className="flex-1 min-w-0">
                                <div className="font-bold text-sm truncate">{t.subject}</div>
                                <div className="text-[11px] text-gray-500 flex flex-wrap items-center gap-2 mt-0.5">
                                    <span className="font-bold text-gray-700">{t.owner_name}</span>
                                    <span>· {TYPE_LABELS[t.type]}</span>
                                    <span>· {t.message_count} Nachr.</span>
                                    <span>· {fmtDate(t.last_activity_at)}</span>
                                </div>
                            </div>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm shrink-0 ${PRIORITY_COLORS[t.priority]}`}>{PRIORITY_LABELS[t.priority]}</span>
                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-sm shrink-0 ${STATUS_COLORS[t.status]}`}>{STATUS_LABELS[t.status]}</span>
                        </button>
                    ))
                )}
            </div>

            {openId && (
                <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 p-4" data-testid="admin-ticket-detail-modal">
                    <div className="bg-white rounded-sm w-full max-w-2xl p-5 max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setOpenId(null)} className="self-end p-1 text-gray-400 hover:text-gray-700 -mt-1 -mr-1" data-testid="admin-ticket-detail-close"><X className="w-5 h-5" /></button>
                        <TicketThread ticketId={openId} onChanged={load} />
                    </div>
                </div>
            )}
        </div>
    );
}
